import { ISearchReynoldsInventoryResponse } from "@/Motivators/api/specialUnits/queries";
import { Editor } from "@tiptap/core";
import { ICursorInfo } from "./textPositionHelpers";
import { TYPE } from "./constants";

export const clearHistoryMap = (searchHistory: Map<string, ISearchReynoldsInventoryResponse[]>, keepHistory = 1) => {
	const historySize = Array.from(searchHistory.keys())?.length;

	if (!historySize || historySize <= keepHistory) {
		return;
	}

	for (const key of Array.from(searchHistory.keys()).slice(0, historySize - keepHistory)) {
		searchHistory.delete(key);
	}
};

const sortString = (str: string) =>
	str
		.split("")
		.sort((a, b) => a.localeCompare(b))
		.join("")
		.trim()
		?.toLowerCase();

export const checkExistingSearch = (input: string, rows: { search: string }[]) => {
	const searchValues = rows.map((item) => item.search).filter((searchValue) => searchValue !== undefined);
	const currentSearch = sortString(input);
	for (const searchValue of searchValues) {
		if (currentSearch === sortString(searchValue)) {
			return true;
		}
	}

	return false;
};

export const generateResultListMessage = (error: { queryError: boolean; backendError: boolean }, noDataFound: boolean) => {
	if (error.queryError) {
		return "The query contains errors";
	}

	if (error.backendError) {
		return "Something went wrong while searching. Please try again later.";
	}

	if (noDataFound) {
		return "No data found";
	}

	return undefined;
};

export const generateCompletionListMessage = (searchText: string, hasItems: boolean, isNumber: boolean) => {
	if (isNumber) {
		return "Please type a number and close the bracket.";
	}

	if (searchText && !hasItems) {
		return `No items found with the text '${searchText}'.`;
	}

	return undefined;
};

export const getValueAndCursor = (editor: Editor) => ({
	currentValue: editor.getText() ?? "",
	index: editor.state.selection.from - 1,
});

export const getCompleteValue = (hasCloseBracket: boolean, value = "") => {
	let completeValue = value;
	if (hasCloseBracket) {
		completeValue = completeValue?.replace(/]$/, "");
	}
	return completeValue;
};

const escapeAndQuote = (value: string) => `"${value.replaceAll('"', '""')}"`;

const prepareKeyPart = (result: ICursorInfo, isNumberType: boolean) => {
	let text = "";
	if (result.autocompleteType === "key" && !result.hasColon) {
		text += ":";
		if (!isNumberType) {
			text += '"';
		}
	}
	return text;
};

const prepareValuePart = (result: ICursorInfo, currentCriteria: string) => {
	let text = "";
	const lastCharBeforeCursor = currentCriteria[result.replaceStart - 1];
	const firstCharAfterCursor = currentCriteria[result.replaceEnd];
	if (result.autocompleteType === "value") {
		if (lastCharBeforeCursor === "(" && firstCharAfterCursor !== ")") {
			text += ")";
		}

		if (!result.hasCloseBracket) {
			text += "]";
			if (/\S/.test(currentCriteria[result.replaceEnd + 2])) {
				text += " ";
			}
		}
	}
	return text;
};

export const prepareAutoCompletion = (autocompleted: string, result: ICursorInfo, isNumberType: boolean, currentCriteria = "") => {
	let text = !result.hasOpenBracket ? "[" : "";

	text += escapeAndQuote(autocompleted);
	text += prepareKeyPart(result, isNumberType);
	text += prepareValuePart(result, currentCriteria);

	if (result.isBetweenBlocks) {
		const atBeginingOfString = result.replaceStart === 0;
		const atEndOfString = result.replaceEnd === currentCriteria.length + 1;
		text = `${atBeginingOfString ? "" : " "}${text}${atEndOfString ? "" : " "}`;
	}

	return text;
};

export const findFirstPrediction = (searchString: string, list: string[] = []) => {
	const foundPrediction = list.find((item) => item.toLowerCase().startsWith(searchString.toLowerCase())) ?? "";
	return { rest: foundPrediction.substring(searchString.length), foundPrediction };
};

const escapeRegExp = (str: string) => str.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

const normalizeRegex = (type: string, value: string) => {
	if (type === TYPE.IDENTIFIER) {
		return `"${value}"`;
	}
	return `"${escapeRegExp(value)}"`;
};

export const getNormalizedQuery = (tokens: { type: string; value: string; normalizedValue?: string }[]) => {
	let normalizedQuery = "";
	let lastTokenIsIdentifier = false;
	for (const token of tokens) {
		if ([TYPE.IDENTIFIER, TYPE.STRING].includes(token.type)) {
			normalizedQuery += token.normalizedValue ?? normalizeRegex(token.type, token.value);
		} else if ([TYPE.LOGICAL, TYPE.OPERATOR].includes(token.type)) {
			normalizedQuery += lastTokenIsIdentifier ? ":" : "";
			normalizedQuery += ` ${token.value} `;
		} else {
			normalizedQuery += token.value;
		}
		lastTokenIsIdentifier = TYPE.IDENTIFIER === token.type;
	}
	return normalizedQuery;
};
